import styled, { useTheme } from 'styled-components'
import { USDC } from '@kunalabs-io/kai'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { Typography } from 'components/Typography/Typography'
import { getDisplayTimestamp, getHealthColor, getPositionStatusLabel } from 'utils/helpers'
import { getAprValue, getEquityUsdValue, getHealthValue, getPnlValue } from './utils'
import { Status } from './MyPositions'
import { ProtocolLpPool } from './components/ProtocolLpPool'
import { PositionRange } from './components/PositionRange'
import { useTokenPrice } from 'utils/useTokenPrice'
import { useRewardsFeesInfo } from 'modules/LpVaultDetails/hooks/useRewardFeeInfos'
import { useRewardsFeesTotalValue } from 'modules/LpVaultDetails/hooks/useRewardsFeesTotalValue'
import { usePositionMetrics } from 'modules/LpVaultDetails/Analytics/hooks/usePositionMetrics'
import { PositionDisplayInfo } from 'utils/userUserPositionDisplayInfos'

interface Props {
  index: number
  onClick?: (positionId: string) => void
  paginatedItems: PositionDisplayInfo[]
  activePositionId?: string
  displayInfo: PositionDisplayInfo
  positionInfo: PositionInfo
}

const Td = styled(Typography)`
  flex-basis: 200px;
  overflow: hidden;
  height: 64px;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: block;
    & > div {
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
    height: auto;
  }
`

const PositionContainer = styled.div`
  & > div > img {
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    & > div > img {
      width: 20px;
      height: 20px;
      margin-right: 4px;
    }
  }
`

const MobileRowWrapper = styled.div<{ $isLast?: boolean; $isActive?: boolean; onClick: (position: number) => void }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  justify-content: space-between;
  border-bottom: ${p => (p.$isLast ? 'none' : `1px solid ${p.theme.colors.divider}`)};

  ${p =>
    p.$isActive &&
    `
     background-color: ${p.theme.colors.background.active};
      & > div > svg > rect {
        fill: ${p.theme.colors.text.primary};
      }
      & > div > svg > path {
        stroke: ${p.theme.colors.background.primary};
      }
    `}
`

export const MobilePositionRow = ({
  index,
  onClick,
  paginatedItems,
  activePositionId,
  displayInfo,
  positionInfo,
}: Props) => {
  const theme = useTheme()

  const { result: rewardsFeesInfos, isLoading: isLoadingRewardsFees } = useRewardsFeesInfo(positionInfo)
  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(positionInfo, rewardsFeesInfos, USDC)

  const { isLoading: isLoadingPositionMetrics, data: positionMetrics } = usePositionMetrics(displayInfo.position.id)
  const { isLoading: isLoadingUsdX, price: priceUsdX } = useTokenPrice(displayInfo.position.X, USDC)
  const { isLoading: isLoadingUsdY, price: priceUsdY } = useTokenPrice(displayInfo.position.Y, USDC)

  return (
    <MobileRowWrapper
      key={index}
      $isLast={index === paginatedItems.length - 1}
      onClick={() => onClick?.(displayInfo.position.id)}
      $isActive={activePositionId === displayInfo.position.id}
    >
      <Td
        style={{
          display: 'block',
          width: '50%',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <PositionContainer>
          <Td variant="caption" style={{ display: 'flex', marginBottom: 8 }}>
            <div style={{ marginRight: 8 }}>Date:</div>
            <div style={{ fontWeight: 600 }}>
              {displayInfo.creationTimestampMs ? getDisplayTimestamp(displayInfo.creationTimestampMs) : ''}
            </div>
          </Td>
          <div style={{ display: 'flex', marginBottom: 8 }}>
            <ProtocolLpPool displayInfo={displayInfo} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 8 }}>
            <Typography variant="caption" style={{ marginRight: 8 }}>
              Status:
            </Typography>
            <Status $type={displayInfo.status}>{getPositionStatusLabel(displayInfo.status)}</Status>
          </div>
          <Td>
            <Typography variant="label1" fontWeight="semiBold" style={{ display: 'flex' }}>
              <div style={{ fontWeight: 400, marginRight: 8 }}>Health:</div>
              {displayInfo.status === 'active' ? (
                <div
                  style={{
                    color: getHealthColor(Number(displayInfo.health!.mul(100).toFixed(0)), theme),
                  }}
                >
                  {getHealthValue(displayInfo.health!)}
                </div>
              ) : (
                '-'
              )}
            </Typography>
          </Td>
        </PositionContainer>
      </Td>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '50%',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <div style={{ width: '100%' }}>
          <Td variant="caption" style={{ display: 'flex', alignItems: 'center', marginBottom: 8, height: 40 }}>
            <PositionRange displayInfo={displayInfo} />
          </Td>
          <Td style={{ marginBottom: 8 }}>
            <Typography
              variant="label1"
              fontWeight="semiBold"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div style={{ fontWeight: 400, marginRight: 8 }}>APR</div>
              <div>{getAprValue(isLoadingPositionMetrics, displayInfo.status, positionMetrics?.apr1h.apr)}</div>
            </Typography>
          </Td>

          <Td style={{ marginBottom: 8 }}>
            <Typography
              variant="label1"
              fontWeight="semiBold"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div style={{ fontWeight: 400, marginRight: 8 }}>Equity</div>
              <div>
                {getEquityUsdValue({
                  status: displayInfo.status,
                  isLoadingUsdX,
                  priceUsdX,
                  equityX: displayInfo.equity.x,
                  isLoadingUsdY,
                  priceUsdY,
                  equityY: displayInfo.equity.y,
                  isLoadingPendingRewards:
                    !isLoadingRewardsFees && rewardsFeesInfos === null
                      ? false
                      : isLoadingRewardsFees || isLoadingTotalValue,
                  pendingRewardsSum: totalSum,
                })}
              </div>
            </Typography>
          </Td>
          <Td>
            <Typography
              variant="label1"
              fontWeight="semiBold"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div style={{ fontWeight: 400, marginRight: 8 }}>PNL:</div>
              <div> {getPnlValue(isLoadingPositionMetrics, positionMetrics?.pnl.totalPnl)}</div>
            </Typography>
          </Td>
        </div>
      </div>
    </MobileRowWrapper>
  )
}
